import { useEffect } from 'react';
import momentTimezone from 'moment-timezone';
import AdapterMoment from '@mui/lab/AdapterMoment';

const getMoment = () => {
  const { moment } = new AdapterMoment({ instance: momentTimezone });
  moment.tz.setDefault('Asia/Kolkata');
  return moment;
};

export const getWeekdayColumnIndex = (day: Date | string): number => {
  const moment = getMoment();
  const firstDayOfWeek = moment.localeData().firstDayOfWeek();
  return (moment(day).day() - firstDayOfWeek + 7) % 7;
};

export const getPickersDayAriaLabel = (
  day: Date | string,
  { isSelected = false, isToday = false }: { isSelected?: boolean; isToday?: boolean } = {}
): string => {
  const moment = getMoment();
  const date = moment(day);
  if (!date.isValid()) {
    return '';
  }
  const parts = [date.format(' MMMM D, YYYY')];
  if (isToday) parts.push('Current date');
  if (isSelected) parts.push('selected');
  return parts.join(', ');
};

export const getSelectedDateAnnouncement = (date: Date | string | null | undefined): string => {
  const moment = getMoment();
  if (!date || !moment(date).isValid()) return '';
  return `${moment(date).format('MMMM D, YYYY')}, selected`;
};

export const getDateInputAriaLabel = (
  label: string,
  value: Date | string | null | undefined,
  emptyLabel = 'Enter date'
): string => {
  const moment = getMoment();
  const parts = label ? [label] : [];
  if (value && moment(value).isValid()) {
    parts.push(getSelectedDateAnnouncement(value));
  } else {
    parts.push(emptyLabel);
  }
  return parts.join(', ');
};

export const enhanceCalendarWeekdayHeaders = (fieldName: string): void => {
  document.querySelectorAll('.MuiPickersCalendar-root').forEach((calendarRoot) => {
    const grid = calendarRoot.querySelector('[role="grid"]');
    if (!grid) return;

    const headerRow = grid.previousElementSibling as HTMLElement | null;
    if (!headerRow?.querySelector('.MuiTypography-caption')) return;

    const weekdayNames = getMoment().weekdays(true);
    headerRow.setAttribute('role', 'row');

    headerRow.querySelectorAll('.MuiTypography-caption').forEach((label, index) => {
      const weekdayName = weekdayNames[index];
      if (!weekdayName) return;

      label.removeAttribute('aria-hidden');
      label.setAttribute('role', 'columnheader');
      label.id = `${fieldName}-weekday-${index}`;
      label.textContent = weekdayName;
    });

    if (!grid.contains(headerRow)) {
      grid.insertBefore(headerRow, grid.firstChild);
    }
  });

  document.querySelectorAll('.MuiPickersDay-hiddenDaySpacingFiller').forEach((filler) => {
    filler.setAttribute('aria-hidden', 'true');
    filler.closest('[role="cell"]')?.setAttribute('aria-hidden', 'true');
  });
};

export const useCalendarWeekdayHeaders = (fieldName: string, isCalendarOpen: boolean): void => {
  useEffect(() => {
    if (!isCalendarOpen) return;

    const enhance = () => enhanceCalendarWeekdayHeaders(fieldName);
    enhance();

    const observer = new MutationObserver(enhance);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [fieldName, isCalendarOpen]);
};
